import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import UpsellDialog from "@/components/UpsellDialog";
import { OptionGroup, MultiChips, SectionTitle } from "@/components/TastingFields";
import {
  LIMPIDEZ, BRILLANTEZ, TIPOS_VINO, INTENSIDAD_7, DENSIDAD, CANTIDAD_3, SI_NO,
  ESTADO_OLF, LIMPIEZA_OLF, PRIMERA_IMPRESION_OLF, INTENSIDAD_5, CARACTER_AROMATICO,
  DEFECTOS, TIPO_BARRICA, NIVEL_TOSTADO, AROMAS, DULZOR, PRIMERA_IMPRESION_GUS,
  EQUILIBRIO, NIVEL_5, ASTRINGENCIA, CUERPO, ESTADO_GUS, ESTILO, CALIDAD_3,
  DESARROLLO, TIPO_AROMAS, INTENSIDAD_SABOR, FINAL_LONGITUD, NIVEL_CALIDAD, CONSUMO_GUARDA, CLIMA,
  colorOptionsFor, categoriaPuntuacion, finalFromCaudalias,
} from "@/lib/tastingOptions";
import { ArrowLeft, ArrowRight, Check, Save, Eye, Wind, Wine as WineIcon, FileText, Award, Loader2, Share2 } from "lucide-react";
import { toast } from "sonner";
import SommChat from "@/components/SommChat";
import { buildTastingContext } from "@/lib/tastingContext";
import { enqueueTasting } from "@/lib/offlineQueue";

const STEPS = [
  { key: "vino", label: "Vino", icon: WineIcon },
  { key: "visual", label: "Visual", icon: Eye },
  { key: "olfativo", label: "Olfativo", icon: Wind },
  { key: "gustativo", label: "Gustativo", icon: WineIcon },
  { key: "conclusion", label: "Conclusión", icon: Award },
];

export default function TastingForm() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [step, setStep] = useState(0);
  const [tastingId, setTastingId] = useState(id || null);
  const [wineId, setWineId] = useState(sp.get("wine") || "");
  const [visual, setVisual] = useState({});
  const [olfativo, setOlfativo] = useState({});
  const [gustativo, setGustativo] = useState({});
  const [obs, setObs] = useState({});
  const [puntuacion, setPuntuacion] = useState(85);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [isPublic, setIsPublic] = useState(false);
  const [savingState, setSavingState] = useState("idle"); // idle | saving | saved
  const [showUpsell, setShowUpsell] = useState(false);
  const dirtyRef = useRef(false);
  const loadedRef = useRef(false);

  const { data: wines = [] } = useQuery({ queryKey: ["wines", ""], queryFn: async () => (await api.get("/wines")).data });
  const { data: usage } = useQuery({ queryKey: ["usage"], queryFn: async () => (await api.get("/usage")).data });
  const blocked = !id && !tastingId && !!usage?.reached;

  const catQueries = {
    ribete_color: useQuery({ queryKey: ["cat", "ribete_color"], queryFn: async () => (await api.get("/catalogs/ribete_color")).data }),
    ribete_anchura: useQuery({ queryKey: ["cat", "ribete_anchura"], queryFn: async () => (await api.get("/catalogs/ribete_anchura")).data }),
    ribete_definicion: useQuery({ queryKey: ["cat", "ribete_definicion"], queryFn: async () => (await api.get("/catalogs/ribete_definicion")).data }),
    lagrimas_cantidad: useQuery({ queryKey: ["cat", "lagrimas_cantidad"], queryFn: async () => (await api.get("/catalogs/lagrimas_cantidad")).data }),
    lagrimas_velocidad: useQuery({ queryKey: ["cat", "lagrimas_velocidad"], queryFn: async () => (await api.get("/catalogs/lagrimas_velocidad")).data }),
    lagrimas_grosor: useQuery({ queryKey: ["cat", "lagrimas_grosor"], queryFn: async () => (await api.get("/catalogs/lagrimas_grosor")).data }),
  };
  const catOptions = (field) => catQueries[field].data || [];
  useEffect(() => {
    if (blocked) setShowUpsell(true);
  }, [blocked]);

  useEffect(() => {
    if (id) {
      api.get(`/tastings/${id}`).then((r) => {
        const t = r.data;
        setWineId(t.wine_id);
        setVisual(t.visual || {});
        setOlfativo(t.olfativo || {});
        setGustativo(t.gustativo || {});
        setObs(t.observaciones || {});
        setPuntuacion(t.puntuacion || 85);
        setFecha(t.fecha_cata || new Date().toISOString().slice(0, 10));
        setIsPublic(!!t.is_public);
        loadedRef.current = true;
      });
    } else {
      loadedRef.current = true;
    }
  }, [id]);

  const vset = (k) => (v) => { setVisual((s) => ({ ...s, [k]: v })); dirtyRef.current = true; };
  const oset = (k) => (v) => { setOlfativo((s) => ({ ...s, [k]: v })); dirtyRef.current = true; };
  const gset = (k) => (v) => { setGustativo((s) => ({ ...s, [k]: v })); dirtyRef.current = true; };
  const obset = (k) => (v) => { setObs((s) => ({ ...s, [k]: v })); dirtyRef.current = true; };

  const mergeSection = (setter, block) => {
    const count = Object.keys(block).length;
    setter((s) => {
      const next = { ...s };
      for (const [k, v] of Object.entries(block)) {
        if (Array.isArray(v)) {
          next[k] = Array.from(new Set([...(Array.isArray(next[k]) ? next[k] : []), ...v]));
        } else {
          next[k] = v;
        }
      }
      return next;
    });
    return count;
  };

  const applySommSuggestion = (s) => {
    let n = 0;
    if (s.visual) n += mergeSection(setVisual, s.visual);
    if (s.olfativo) n += mergeSection(setOlfativo, s.olfativo);
    if (s.gustativo) n += mergeSection(setGustativo, s.gustativo);
    if (s.obs) n += mergeSection(setObs, s.obs);
    if (n > 0) dirtyRef.current = true;
    return n;
  };

  const buildPayload = useCallback((status) => ({
    wine_id: wineId, status, visual, olfativo, gustativo,
    observaciones: obs, puntuacion,
    is_public: isPublic,
    fecha_cata: fecha || new Date().toISOString().slice(0, 10),
  }), [wineId, visual, olfativo, gustativo, obs, puntuacion, fecha, isPublic]);

  const doSave = useCallback(async (status) => {
    if (!wineId) return null;
    const payload = buildPayload(status);
    if (tastingId) {
      await api.put(`/tastings/${tastingId}`, payload);
      return tastingId;
    }
    const { data } = await api.post("/tastings", payload);
    setTastingId(data.id);
    return data.id;
  }, [wineId, tastingId, buildPayload]);

  const handleSaveError = (err) => {
    if (err?.response?.status === 403) { setShowUpsell(true); return true; }
    return false;
  };

  const isOffline = (err) => !navigator.onLine || !err?.response;

  const saveOffline = async (status) => {
    await enqueueTasting({
      type: tastingId ? "update" : "create",
      tastingId,
      payload: buildPayload(status),
    });
  };

  // Autosave (draft) with debounce
  useEffect(() => {
    if (!loadedRef.current || !wineId || !dirtyRef.current || blocked) return;
    setSavingState("saving");
    const timer = setTimeout(async () => {
      try {
        await doSave("borrador");
        dirtyRef.current = false;
        setSavingState("saved");
        setTimeout(() => setSavingState("idle"), 1500);
      } catch (err) {
        setSavingState("idle");
        handleSaveError(err);
      }
    }, 1600);
    return () => clearTimeout(timer);
  }, [doSave, wineId, blocked]);

  const saveDraft = async () => {
    if (!wineId) { toast.error("Selecciona un vino primero"); return; }
    if (blocked) { setShowUpsell(true); return; }
    try { await doSave("borrador"); qc.invalidateQueries({ queryKey: ["cat"] }); qc.invalidateQueries({ queryKey: ["usage"] }); toast.success("Borrador guardado"); }
    catch (err) {
      if (handleSaveError(err)) return;
      if (isOffline(err)) { await saveOffline("borrador"); toast.success("Guardado sin conexión. Se sincronizará al recuperar internet."); return; }
      toast.error("No se pudo guardar");
    }
  };

  const finalize = async () => {
    if (!wineId) { toast.error("Selecciona un vino primero"); return; }
    if (blocked) { setShowUpsell(true); return; }
    try {
      const tid = await doSave("completa");
      qc.invalidateQueries({ queryKey: ["cat"] });
      qc.invalidateQueries({ queryKey: ["usage"] });
      qc.invalidateQueries({ queryKey: ["wines"] });
      toast.success("Cata guardada");
      navigate(`/app/catas/${tid}`);
    } catch (err) {
      if (handleSaveError(err)) return;
      if (isOffline(err)) {
        await saveOffline("completa");
        toast.success("Cata guardada sin conexión. Se sincronizará automáticamente al recuperar internet.");
        navigate("/app/historial");
        return;
      }
      toast.error("No se pudo guardar la cata");
    }
  };

  const cat = categoriaPuntuacion(puntuacion);
  const colorOpts = colorOptionsFor(visual.tipo);

  return (
    <div className="max-w-3xl mx-auto animate-fade-up pb-10">
      <UpsellDialog open={showUpsell} onOpenChange={setShowUpsell} count={usage?.count ?? 10} limit={usage?.limit ?? 10} />
      <button data-testid="tasting-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-grises-500 hover:text-borgona-700 mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Volver
      </button>
      <div className="flex items-center justify-between mb-2">
        <h1 className="font-serif text-4xl text-negro">{id ? "Editar cata" : "Nueva cata"}</h1>
        <div className="flex items-center gap-3">
          {usage && usage.limit !== null && (
            <span data-testid="usage-badge" className={`text-xs px-2.5 py-1 rounded-full font-medium ${usage.reached ? "bg-borgona-50 text-borgona-700" : "bg-grises-100 text-grises-500"}`}>
              {usage.count}/{usage.limit} catas gratis
            </span>
          )}
          <div className="text-xs text-grises-400 h-5 flex items-center gap-1.5" data-testid="autosave-indicator">
            {savingState === "saving" && <><Loader2 className="w-3 h-3 animate-spin" /> Guardando...</>}
            {savingState === "saved" && <><Check className="w-3 h-3 text-dorado-700" /> Guardado</>}
          </div>
        </div>
      </div>

      {blocked && (
        <div data-testid="limit-banner" className="mb-6 rounded-xl border border-borgona-700/20 bg-borgona-50 px-5 py-4 flex items-center justify-between gap-4">
          <p className="text-sm text-borgona-900">Alcanzaste el límite de {usage?.limit} catas del plan gratuito. Suscríbete al plan anual para seguir registrando.</p>
          <Button data-testid="banner-upsell-btn" onClick={() => setShowUpsell(true)} className="bg-dorado-500 text-negro hover:bg-dorado-700 rounded-full shrink-0 text-sm">Ver plan anual</Button>
        </div>
      )}

      {/* Stepper */}
      <div className="flex items-center gap-1 sm:gap-2 mb-8 overflow-x-auto pb-2">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const done = i < step;
          const active = i === step;
          return (
            <React.Fragment key={s.key}>
              <button
                data-testid={`step-${s.key}`}
                onClick={() => (wineId || i === 0) ? setStep(i) : toast.error("Selecciona un vino primero")}
                className="flex items-center gap-2 shrink-0"
              >
                <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm transition-colors duration-300 ${
                  active ? "bg-borgona-700 text-blanco" : done ? "bg-dorado-500 text-negro" : "bg-grises-100 text-grises-400"
                }`}>
                  {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
                </span>
                <span className={`text-sm hidden sm:inline ${active ? "text-negro font-medium" : "text-grises-400"}`}>{s.label}</span>
              </button>
              {i < STEPS.length - 1 && <div className="h-px flex-1 min-w-[12px] bg-grises-200" />}
            </React.Fragment>
          );
        })}
      </div>

      <Card className="p-6 sm:p-8 border-grises-200 rounded-xl space-y-6">
        {/* STEP 0 - WINE */}
        {step === 0 && (
          <div className="space-y-4">
            <Label className="text-grises-600">Vino a catar *</Label>
            <Select value={wineId} onValueChange={(v) => { setWineId(v); }}>
              <SelectTrigger data-testid="tasting-wine-select" className="h-11"><SelectValue placeholder="Selecciona un vino" /></SelectTrigger>
              <SelectContent>
                {wines.map((w) => <SelectItem key={w.id} value={w.id}>{w.nombre} — {w.bodega || "s/bodega"} {w.anada}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-sm text-grises-500">
              ¿No aparece tu vino?{" "}
              <button data-testid="create-wine-inline" onClick={() => navigate("/app/vinos/nuevo")} className="text-borgona-700 hover:underline">Regístralo aquí</button>
            </p>
            <div>
              <Label className="text-grises-600">Fecha de cata</Label>
              <Input
                data-testid="tasting-fecha-input"
                type="date"
                value={fecha}
                onChange={(e) => { setFecha(e.target.value); dirtyRef.current = true; }}
                className="mt-1.5 h-11 max-w-xs"
              />
            </div>
            <div className={`rounded-xl border-2 p-4 flex items-start justify-between gap-4 transition-colors ${isPublic ? "border-borgona-500 bg-borgona-50/60" : "border-grises-300 bg-grises-50"}`}>
              <div>
                <Label className="text-grises-700 font-medium flex items-center gap-2">
                  <Share2 className="w-4 h-4 text-borgona-700" /> Compartir en la comunidad
                </Label>
                <p className="text-sm text-grises-500 mt-1">
                  Si la activas, esta cata aparecerá en <strong>Catas Compartidas</strong> y otros usuarios con suscripción podrán verla y reaccionar (no pueden comentarla).
                </p>
                <span data-testid="tasting-public-state" className={`inline-block mt-2 text-xs font-medium px-2 py-0.5 rounded-full ${isPublic ? "bg-borgona-700 text-blanco" : "bg-grises-200 text-grises-600"}`}>
                  {isPublic ? "Pública" : "Privada"}
                </span>
              </div>
              <Switch
                data-testid="tasting-public-toggle"
                checked={isPublic}
                onCheckedChange={(v) => { setIsPublic(v); dirtyRef.current = true; }}
                className="mt-1 border-grises-400 data-[state=unchecked]:bg-grises-300 data-[state=checked]:bg-borgona-700"
              />
            </div>
          </div>
        )}

        {/* STEP 1 - VISUAL */}
        {step === 1 && (
          <div className="space-y-6">
            <SectionTitle>Examen Visual</SectionTitle>
            <OptionGroup label="Limpidez" options={LIMPIDEZ} value={visual.limpidez} onChange={vset("limpidez")} testId="visual-limpidez" />
            <OptionGroup label="Brillantez" options={BRILLANTEZ} value={visual.brillantez} onChange={vset("brillantez")} testId="visual-brillantez" />
            <OptionGroup label="Tipo" options={TIPOS_VINO.slice(0, 3)} value={visual.tipo} onChange={vset("tipo")} testId="visual-tipo" />
            <OptionGroup label="Color" options={colorOpts} value={visual.color} onChange={vset("color")} testId="visual-color" />
            <OptionGroup label="Intensidad" options={INTENSIDAD_7} value={visual.intensidad} onChange={vset("intensidad")} testId="visual-intensidad" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><Label className="text-grises-600 text-sm">Ribete · Color</Label><Input list="dl-ribete_color" value={visual.ribete_color || ""} onChange={(e) => vset("ribete_color")(e.target.value)} className="mt-1.5 h-10" /><datalist id="dl-ribete_color">{catOptions("ribete_color").map((x) => <option key={x} value={x} />)}</datalist></div>
              <div><Label className="text-grises-600 text-sm">Ribete · Anchura</Label><Input list="dl-ribete_anchura" value={visual.ribete_anchura || ""} onChange={(e) => vset("ribete_anchura")(e.target.value)} className="mt-1.5 h-10" /><datalist id="dl-ribete_anchura">{catOptions("ribete_anchura").map((x) => <option key={x} value={x} />)}</datalist></div>
              <div><Label className="text-grises-600 text-sm">Ribete · Definición</Label><Input list="dl-ribete_definicion" value={visual.ribete_definicion || ""} onChange={(e) => vset("ribete_definicion")(e.target.value)} className="mt-1.5 h-10" /><datalist id="dl-ribete_definicion">{catOptions("ribete_definicion").map((x) => <option key={x} value={x} />)}</datalist></div>
            </div>
            <OptionGroup label="Densidad aparente" options={DENSIDAD} value={visual.densidad} onChange={vset("densidad")} testId="visual-densidad" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div><Label className="text-grises-600 text-sm">Lágrimas · Cantidad</Label><Input list="dl-lagrimas_cantidad" value={visual.lagrimas_cantidad || ""} onChange={(e) => vset("lagrimas_cantidad")(e.target.value)} className="mt-1.5 h-10" /><datalist id="dl-lagrimas_cantidad">{catOptions("lagrimas_cantidad").map((x) => <option key={x} value={x} />)}</datalist></div>
              <div><Label className="text-grises-600 text-sm">Lágrimas · Velocidad</Label><Input list="dl-lagrimas_velocidad" value={visual.lagrimas_velocidad || ""} onChange={(e) => vset("lagrimas_velocidad")(e.target.value)} className="mt-1.5 h-10" /><datalist id="dl-lagrimas_velocidad">{catOptions("lagrimas_velocidad").map((x) => <option key={x} value={x} />)}</datalist></div>
              <div><Label className="text-grises-600 text-sm">Lágrimas · Grosor</Label><Input list="dl-lagrimas_grosor" value={visual.lagrimas_grosor || ""} onChange={(e) => vset("lagrimas_grosor")(e.target.value)} className="mt-1.5 h-10" /><datalist id="dl-lagrimas_grosor">{catOptions("lagrimas_grosor").map((x) => <option key={x} value={x} />)}</datalist></div>
            </div>
            <OptionGroup label="Gas Carbónico · Presencia" options={SI_NO} value={visual.gas_presencia} onChange={vset("gas_presencia")} testId="visual-gas" />
            <OptionGroup label="Sedimentos" options={SI_NO} value={visual.sedimentos} onChange={vset("sedimentos")} testId="visual-sedimentos" />
            <div>
              <Label className="text-grises-600">Observaciones visuales</Label>
              <Textarea data-testid="visual-obs" value={visual.observaciones || ""} onChange={(e) => vset("observaciones")(e.target.value)} className="mt-1.5" rows={2} />
            </div>
          </div>
        )}

        {/* STEP 2 - OLFATIVO */}
        {step === 2 && (
          <div className="space-y-6">
            <SectionTitle>Examen Olfativo</SectionTitle>
            <OptionGroup label="Primera nariz · Estado" options={ESTADO_OLF} value={olfativo.estado} onChange={oset("estado")} testId="olf-estado" />
            <OptionGroup label="Limpieza" options={LIMPIEZA_OLF} value={olfativo.limpieza} onChange={oset("limpieza")} testId="olf-limpieza" />
            <OptionGroup label="Primera impresión" options={PRIMERA_IMPRESION_OLF} value={olfativo.primera_impresion} onChange={oset("primera_impresion")} testId="olf-primera" />
            <OptionGroup label="Intensidad" options={INTENSIDAD_5} value={olfativo.intensidad} onChange={oset("intensidad")} testId="olf-intensidad" />
            <OptionGroup label="Desarrollo / Evolución" options={DESARROLLO} value={olfativo.desarrollo} onChange={oset("desarrollo")} testId="olf-desarrollo" />
            <MultiChips label="Carácter aromático" options={CARACTER_AROMATICO} value={olfativo.caracter} onChange={oset("caracter")} testId="olf-caracter" />
            <MultiChips label="Defectos" options={DEFECTOS} value={olfativo.defectos} onChange={oset("defectos")} testId="olf-defectos" />
            <OptionGroup label="Paso por barrica" options={SI_NO} value={olfativo.barrica} onChange={oset("barrica")} testId="olf-barrica" />
            {olfativo.barrica === "Sí" && (
              <>
                <OptionGroup label="Tipo de barrica" options={TIPO_BARRICA} value={olfativo.barrica_tipo} onChange={oset("barrica_tipo")} testId="olf-barrica-tipo" />
                <OptionGroup label="Nivel de tostado" options={NIVEL_TOSTADO} value={olfativo.tostado} onChange={oset("tostado")} testId="olf-tostado" />
              </>
            )}
            <div className="pt-2">
              <SectionTitle>Aromas</SectionTitle>
              <MultiChips label="Tipo de aromas" options={TIPO_AROMAS} value={olfativo.tipo_aromas} onChange={oset("tipo_aromas")} testId="olf-tipo-aromas" />
              <div className="space-y-4 mt-4">
                {Object.entries(AROMAS).map(([fam, items]) => (
                  <MultiChips key={fam} label={fam} options={items} value={olfativo[`aromas_${fam}`]} onChange={oset(`aromas_${fam}`)} testId={`olf-aromas-${fam.toLowerCase()}`} />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 - GUSTATIVO */}
        {step === 3 && (
          <div className="space-y-6">
            <SectionTitle>Examen Gustativo</SectionTitle>
            <OptionGroup label="Ataque · Dulzor" options={DULZOR} value={gustativo.dulzor} onChange={gset("dulzor")} testId="gus-dulzor" />
            <OptionGroup label="Primera impresión" options={PRIMERA_IMPRESION_GUS} value={gustativo.primera_impresion} onChange={gset("primera_impresion")} testId="gus-primera" />
            <OptionGroup label="Equilibrio" options={EQUILIBRIO} value={gustativo.equilibrio} onChange={gset("equilibrio")} testId="gus-equilibrio" />
            <OptionGroup label="Acidez" options={INTENSIDAD_5} value={gustativo.acidez} onChange={gset("acidez")} testId="gus-acidez" />
            <OptionGroup label="Alcohol" options={NIVEL_5} value={gustativo.alcohol} onChange={gset("alcohol")} testId="gus-alcohol" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <OptionGroup label="Taninos · Cantidad" options={CANTIDAD_3} value={gustativo.taninos_cantidad} onChange={gset("taninos_cantidad")} testId="gus-taninos-cant" />
              <OptionGroup label="Taninos · Calidad" options={CALIDAD_3} value={gustativo.taninos_calidad} onChange={gset("taninos_calidad")} testId="gus-taninos-cal" />
            </div>
            <OptionGroup label="Astringencia" options={ASTRINGENCIA} value={gustativo.astringencia} onChange={gset("astringencia")} testId="gus-astringencia" />
            <OptionGroup label="Cuerpo" options={CUERPO} value={gustativo.cuerpo} onChange={gset("cuerpo")} testId="gus-cuerpo" />
            <OptionGroup label="Intensidad de sabor" options={INTENSIDAD_SABOR} value={gustativo.intensidad_sabor} onChange={gset("intensidad_sabor")} testId="gus-intensidad-sabor" />
            <div><Label className="text-grises-600 text-sm">Persistencia (caudalías)</Label><Input type="number" value={gustativo.persistencia || ""} onChange={(e) => { const v = e.target.value; const fin = finalFromCaudalias(v); setGustativo((s) => ({ ...s, persistencia: v, final: fin || s.final })); dirtyRef.current = true; }} className="mt-1.5 h-10" /></div>
            <OptionGroup label="Final / Persistencia — longitud" options={FINAL_LONGITUD} value={gustativo.final} onChange={gset("final")} testId="gus-final" />
            <div><Label className="text-grises-600 text-sm">Temperatura de servicio</Label><Input value={gustativo.temperatura || ""} onChange={(e) => gset("temperatura")(e.target.value)} className="mt-1.5 h-10" placeholder="16-18°C" /></div>
            <OptionGroup label="Estado" options={ESTADO_GUS} value={gustativo.estado} onChange={gset("estado")} testId="gus-estado" />
            <OptionGroup label="Estilo" options={ESTILO} value={gustativo.estilo} onChange={gset("estilo")} testId="gus-estilo" />
            <OptionGroup label="¿Decantar?" options={SI_NO} value={gustativo.decantar} onChange={gset("decantar")} testId="gus-decantar" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label className="text-grises-600 text-sm">Potencial de guarda</Label><Input value={gustativo.potencial_guarda || ""} onChange={(e) => gset("potencial_guarda")(e.target.value)} className="mt-1.5 h-10" placeholder="5-10 años" /></div>
              <div><Label className="text-grises-600 text-sm">Maridaje</Label><Input value={gustativo.maridaje || ""} onChange={(e) => gset("maridaje")(e.target.value)} className="mt-1.5 h-10" placeholder="Carnes rojas, quesos..." /></div>
            </div>

            <div className="pt-4 border-t border-grises-200">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-grises-600">Evaluación (0-100)</Label>
                <div className="text-right">
                  <div className="font-mono text-4xl font-bold" style={{ color: cat.color }} data-testid="score-value">{puntuacion}</div>
                  <div className="text-xs uppercase tracking-wider font-medium" style={{ color: cat.color }} data-testid="score-category">{cat.label}</div>
                </div>
              </div>
              <div className="vinote-slider">
                <Slider data-testid="score-slider" value={[puntuacion]} min={0} max={100} step={1}
                  onValueChange={(v) => { setPuntuacion(v[0]); dirtyRef.current = true; }} />
              </div>
            </div>
          </div>
        )}

        {/* STEP 4 - CONCLUSIÓN */}
        {step === 4 && (
          <div className="space-y-5">
            <SectionTitle>Conclusión</SectionTitle>

            {/* Resumen de calidad (derivado de la evaluación) */}
            <div className="flex items-center justify-between rounded-xl border border-grises-200 bg-grises-50 px-5 py-4">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-grises-500">Evaluación</p>
                <p className="text-sm text-grises-600">Puntuación asignada en la fase gustativa</p>
              </div>
              <div className="text-right">
                <div className="font-mono text-3xl font-bold" style={{ color: cat.color }}>{puntuacion}</div>
                <div className="text-xs uppercase tracking-wider font-medium" style={{ color: cat.color }}>{cat.label}</div>
              </div>
            </div>

            <OptionGroup label="Nivel de calidad" options={NIVEL_CALIDAD} value={obs.calidad} onChange={obset("calidad")} testId="con-calidad" />
            <OptionGroup label="Consumo / potencial de guarda" options={CONSUMO_GUARDA} value={obs.consumo} onChange={obset("consumo")} testId="con-consumo" />

            <div className="pt-2">
              <SectionTitle>Conclusiones a ciegas</SectionTitle>
              <div className="space-y-4">
                <OptionGroup label="Clima estimado" options={CLIMA} value={obs.clima} onChange={obset("clima")} testId="con-clima" />
                <OptionGroup label="Mundo / Estilo" options={ESTILO} value={obs.mundo} onChange={obset("mundo")} testId="con-mundo" />
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div><Label className="text-grises-600 text-sm">Variedad probable</Label><Input data-testid="con-variedad-probable" value={obs.variedad_probable || ""} onChange={(e) => obset("variedad_probable")(e.target.value)} className="mt-1.5 h-10" /></div>
                  <div><Label className="text-grises-600 text-sm">País / Región probable</Label><Input data-testid="con-origen-probable" value={obs.origen_probable || ""} onChange={(e) => obset("origen_probable")(e.target.value)} className="mt-1.5 h-10" /></div>
                  <div><Label className="text-grises-600 text-sm">Añada estimada</Label><Input data-testid="con-anada-probable" value={obs.anada_probable || ""} onChange={(e) => obset("anada_probable")(e.target.value)} className="mt-1.5 h-10" /></div>
                </div>
              </div>
            </div>

            <div className="pt-2">
              <SectionTitle>Notas y observaciones</SectionTitle>
              <div className="space-y-4">
                <div>
                  <Label className="text-grises-600">Notas libres</Label>
                  <Textarea data-testid="obs-notas" value={obs.notas || ""} onChange={(e) => obset("notas")(e.target.value)} rows={5} className="mt-1.5" placeholder="Describe tu experiencia con este vino..." />
                </div>
                <div>
                  <Label className="text-grises-600">Evolución del vino</Label>
                  <Textarea data-testid="obs-evolucion" value={obs.evolucion || ""} onChange={(e) => obset("evolucion")(e.target.value)} rows={2} className="mt-1.5" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><Label className="text-grises-600 text-sm">Momento ideal de consumo</Label><Input value={obs.momento || ""} onChange={(e) => obset("momento")(e.target.value)} className="mt-1.5 h-10" /></div>
                  <div><Label className="text-grises-600 text-sm">Relación calidad-precio</Label><Input value={obs.relacion_precio || ""} onChange={(e) => obset("relacion_precio")(e.target.value)} className="mt-1.5 h-10" /></div>
                </div>
                <div>
                  <Label className="text-grises-600">Recomendaciones</Label>
                  <Textarea data-testid="obs-recomendaciones" value={obs.recomendaciones || ""} onChange={(e) => obset("recomendaciones")(e.target.value)} rows={2} className="mt-1.5" />
                </div>
                <OptionGroup label="¿Lo volverías a comprar?" options={SI_NO} value={obs.volveria} onChange={obset("volveria")} testId="obs-volveria" />
                <div>
                  <Label className="text-grises-600">Etiquetas personales (separadas por coma)</Label>
                  <Input data-testid="obs-etiquetas" value={obs.etiquetas || ""} onChange={(e) => obset("etiquetas")(e.target.value)} className="mt-1.5 h-10" placeholder="favorito, ocasión especial, regalo" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between pt-6 border-t border-grises-200">
          <Button variant="outline" disabled={step === 0} onClick={() => setStep((s) => s - 1)}
            className="rounded-full border-grises-300" data-testid="prev-step-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={saveDraft} className="rounded-full border-borgona-700 text-borgona-700 hover:bg-borgona-50" data-testid="save-draft-btn">
              <Save className="w-4 h-4 mr-1" /> Borrador
            </Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => { if (!wineId) { toast.error("Selecciona un vino"); return; } setStep((s) => s + 1); }}
                className="bg-borgona-700 hover:bg-borgona-900 text-blanco rounded-full transition-colors duration-300" data-testid="next-step-btn">
                Siguiente <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button onClick={finalize} className="bg-dorado-500 text-negro hover:bg-dorado-700 rounded-full font-medium transition-colors duration-300" data-testid="finalize-tasting-btn">
                <Check className="w-4 h-4 mr-1" /> Finalizar cata
              </Button>
            )}
          </div>
        </div>
      </Card>
      <SommChat
        sessionId={tastingId || "nueva-cata"}
        onApply={applySommSuggestion}
        getContext={() => buildTastingContext(
          wines.find((w) => w.id === wineId) || {},
          { visual, olfativo, gustativo, observaciones: obs, puntuacion }
        )}
      />
    </div>
  );
}
